<script>
	import { onMount } from 'svelte'
	import { slide } from 'svelte/transition'

	import Message from './Message.svelte'
	import LineItems from './LineItems.svelte'
	import CartTotals from './CartTotals.svelte'
	import { group_line_items } from './cart_functions'

	const api = make_api()

	let cart_loading
	let inited = false
	let cart_message
	let totals_loading
	let coupon_message
	let giftcard_message
	let empty_cart

	let line_items = {}
	let removed_line_items = {}
	let coupons_enabled
	let subtotal
	let total
	let coupon_total
	let coupons
	let giftcard_payment
	let giftcards
	let shipping_price
	let tax_total
	let time
	let local_notices = []
	let wc_notices = []

	let grouped_line_items = []
	let prev_item_keys = []

	async function cart_api(route, data) {
		cart_loading = true
		return await api(route, data).finally(() => {
			cart_loading = false
		})
	}

	async function totals_api(route, data) {
		totals_loading = true
		return await api(route, data).finally(() => {
			totals_loading = false
		})
	}

	function make_api() {
		const nonce_promise = fetch(`/wp-admin/admin-ajax.php?action=rest-nonce`)
			.then(res => res.text())
			.then(nonce => nonce.trim()) // NOTE: trim() is important here, removing will break prod!

		return async function api(route, data) {
			const nonce = await nonce_promise

			const cart_endpoint = nonce !== `0`
				? `/wp-json/cart${ route }?_wpnonce=${ nonce }`
				: `/wp-json/cart${ route }`

			const res = await fetch(cart_endpoint, {
				method: `POST`,
				headers: {
					Accept: `application/json`,
					"Content-Type": `application/json; charset=utf-8`,
				},
				body: JSON.stringify(data),
			})

			const text = await res.text()
			try {
				// 500 responses can be parsed...
				const cart = JSON.parse(text)

				coupon_message = cart.coupon_message
				giftcard_message = cart.giftcard_message
				cart_message = cart.cart_message

				if (res.status < 200 || res.status >= 300) {
					const error = new Error(cart_message?.text || `http response status was ${ res.status } (${ route })`)
					error.status = res.status
					throw error
				}

				line_items = cart.line_items
				removed_line_items = cart.removed_line_items
				coupons_enabled = cart.coupons_enabled
				subtotal = cart.subtotal
				total = cart.total
				coupon_total = cart.coupon_total
				coupons = cart.coupons
				giftcard_payment = cart.giftcard_payment
				giftcards = cart.giftcards
				shipping_price = cart.shipping_price
				tax_total = cart.tax_total
				time = cart.time
				wc_notices = cart.wc_notices

				let items = {}

				for (let key of prev_item_keys) {
					if (removed_line_items[key]) {
						items[key] = removed_line_items[key]
					}
					if (line_items[key]) {
						items[key] = line_items[key]
					}
				}

				for (let key of Object.keys(line_items)) {
					if (!items[key]) {
						items[key] = line_items[key]
					}
				}

				prev_item_keys = Object.keys(items)
				grouped_line_items = group_line_items(items)
				empty_cart = !Object.keys(line_items).length
			} catch (e) {
				if (e.status === undefined || e.status === 500) {
					local_notices = [ ...local_notices, { type: `error`, text: e.message }]
				}
				console.error(e)
			}
		}
	}


	onMount(() => {
		cart_api(`/init`, {}).then(() => {
			inited = true
		})
	})

	$: notices = [ ...local_notices, ...wc_notices ]
</script>

<div class="cart">
	{#if notices.length }
		<div class="notices" transition:slide|global>
			{#each notices as notice }
				{#if notice.html}
					<div class="notice {notice.type}">{@html notice.html}</div>
				{:else}
					<div class="notice {notice.type}">{notice.text}</div>
				{/if}
			{/each}
		</div>
	{/if}

	<div class="loading" hidden="{!cart_loading}"></div>
	<div class="disabled" hidden="{!cart_loading}"></div>

	{#if inited}

	{#if empty_cart}
		<div class="empty">Your cart is currently empty.</div>
	{:else}

		<LineItems
			api={cart_api}
			{time}
			grouped_line_items={grouped_line_items}
		/>

		<div class="review">

			<div class="left">
				<Message bind:message={cart_message} />
			</div>

			<div class="totals">
				<div class="loading" hidden="{!totals_loading}"></div>
				<div class="disabled" hidden="{!totals_loading}"></div>

				<div class="title">Cart Totals</div>

				<CartTotals
					{subtotal}
					{shipping_price}
					{coupons_enabled}
					{coupons}
					{giftcards}
					{giftcard_payment}
					{total}
					api={totals_api}
					{coupon_total}
					{tax_total}
					{coupon_message}
					{giftcard_message}
				/>
				<a class="checkout" href="/checkout">Proceed to Checkout</a>
			</div>

		</div>

	{/if}
	{/if}
</div>



<style>
	.cart {
		position: relative;
		font-size: 16px;
		padding: 0 16px;
		min-height: 500px;
	}

	@media (max-width: 600px) {
		.cart {
			font-size: 14px;
			padding: 0 5px;
		}
	}

	.empty {
		padding: 20px;
		text-align: center;
	}

	.disabled {
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		z-index: 50;
		background: rgba(8, 8, 8, .6);
	}

	.loading {
		position: fixed;
		left: 0;
		right: 0;
		margin: 0 auto;
		top: 50%;
		margin-top: -50px;
		height: 100px;
		width: 100px;
		z-index: 51;
		background: #000 url('../../img/loading_100.gif');
		border-radius: 100%;
	}

	/* notices are similar to <Message />s */
	.notices {
		font-weight: 400 !important;
	}

	.notices .notice {
		padding: 10px 20px;
		font-size: 90%;
	}

	.notices .error {
		background: #a90707;
		color: #fff;
	}

	.notices .success {
		background: #ceff51;
		color: #000;
		border-radius: var(--default-border-radius);
	}

	.review {
		display: flex;
		flex-direction: row;
		margin-top: 20px;
	}

	.review .left,
	.review .totals {
		width: 100%;
	}

	.review .left {
		margin-right: 30px;
	}

	@media (max-width: 700px) {
		.review {
			flex-direction: column;
		}
		.review .left,
		.review .totals {
			max-width: 500px;
			margin: 0 auto;
		}
	}

	.review .totals {
		position: relative;
	}

	.review .totals .loading:not([hidden]) {
		position: absolute;
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.review .totals .title {
		color: #ceff51;
		font-size: 150%;
		text-transform: uppercase;
	}

	.checkout {
		letter-spacing: 1px;
		margin: 20px 0;
		display: block;
		text-align: center;
		font-size: 80%;
		text-transform: uppercase;
		color: #000;
		background: #ceff51;
		padding: 10px 20px;
		border-radius: var(--default-border-radius);
	}


	a:focus-visible {
		outline: 3px solid #fff;
	}
</style>
